import {
  faBank,
  faFileImport,
  faMobileScreen,
  faPlusCircle,
  faUserGroupSimple,
} from '@fortawesome/pro-duotone-svg-icons'
import {
  faAddressBook,
  faBadgePercent,
  faBell,
  faBrowser,
  faCalendarCheck,
  faCalendarCircleUser,
  faCalendarStar,
  faCogs,
  faExternalLink,
  faFile,
  faFileChartColumn,
  faFileContract,
  faFileInvoiceDollar,
  faFilter,
  faHandHoldingDollar,
  faInbox,
  faListAlt,
  faMoneyCheck,
  faPlus,
  faRocket,
  faRunning,
  faShoppingCart,
  faSync,
  faTags,
  faUndoAlt,
  faUnlock,
  faUserCog,
  faUserFriends,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Command, useCommandState } from 'cmdk'
import { DateTime } from 'luxon'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { DefaultDozenPagination } from '../../../constants/pagination'
import { ROUTES } from '../../../constants/routes'
import { useAuth } from '../../../hooks/useAuth'
import { useFetchUserMembers } from '../../../hooks/useFetchUserMember'
import { useFetchInfiniteAttendees } from '../../../pages/Attendees/pages/ManageAttendees/hooks/useFetchAttendees'
import { useFetchInfiniteContacts } from '../../../pages/Contacts/pages/ManageContacts/hooks/useFetchContacts'
import { AppointmentSchedulingText } from '../../../pages/Events/pages/ManageEvents/components/AppointmentSchedulingText'
import { AppointmentTypeStatusBadge } from '../../../pages/Events/pages/ManageEvents/components/AppointmentTypeStatusBadge'
import { useFetchInfiniteAppointmentTypes } from '../../../pages/Events/pages/ManageEvents/hooks/useFetchAppointmentTypes'
import { useFetchInfiniteEvents } from '../../../pages/Events/pages/ManageEvents/hooks/useFetchEvents'
import { FinancialReportList } from '../../../pages/Financials/pages/FinancialReports/FinancialReports'
import { useFetchInfiniteCoupons } from '../../../pages/Financials/pages/ManageCoupons/hooks/useFetchCoupons'
import { useFetchInfiniteCouponsV2 } from '../../../pages/Financials/pages/ManageCoupons/hooks/useFetchCouponsV2'
import { MembershipStatusBadge } from '../../../pages/Financials/pages/ManageMemberships/components/MembershipStatusBadge'
import { useFetchInfiniteMemberships } from '../../../pages/Financials/pages/ManageMemberships/hooks/useFetchMemberships'
import { useFetchInfiniteOrders } from '../../../pages/Financials/pages/ManageOrders/hooks/useFetchOrders'
import { useFetchInfiniteOrdersV2 } from '../../../pages/Financials/pages/ManageOrders/hooks/useFetchOrdersV2'
import { useFetchInfiniteTaxRates } from '../../../pages/Financials/pages/ManageTaxRates/hooks/useFetchTaxRates'
import { useFetchInfiniteTaxRatesV2 } from '../../../pages/Financials/pages/ManageTaxRates/hooks/useFetchTaxRatesV2'
import { useFetchConversations } from '../../../pages/Inbox/pages/Inbox/hooks/useFetchConversations'
import {
  formatAge,
  formatDefaultLabel,
  formatFullName,
  formatGender,
  formatList,
} from '../../../utils/formatters'
import { CouponDescription } from '../../Coupon/CouponDescription'
import { CouponDescriptionV2 } from '../../Coupon/CouponDescriptionV2'
import { FeatureFlag } from '../../FeatureFlag/FeatureFlag'
import { useFlags } from '../../FeatureFlag/hooks/useFlags'
import { Icon } from '../../Icon/Icon'
import { OrderStatusBadge } from '../../OrderStatusBadge/OrderStatusBadge'
import { OrderStatusBadgeV2 } from '../../OrderStatusBadge/OrderStatusBadgeV2'
import { TruncatedText } from '../../TruncatedText/TruncatedText'
import { ProvideCommandRoute, useCommandReroute } from '../hooks/useCommandReroute'

type PageType =
  | 'home'
  | 'orders'
  | 'tax rates'
  | 'coupons'
  | 'memberships'
  | 'events'
  | 'appointment types'
  | 'contacts'
  | 'attendees'
  | 'conversations'
  | 'reports'
  | 'programs'

interface ICommandNavProps {
  page?: PageType
  onReroute?: Function
}

export const CommmandNav = ({ page, onReroute: handleReroute }: ICommandNavProps) => {
  const { flags } = useFlags()
  const isProductsEnabled = !!flags?.products_enabled
  const ref = useRef<HTMLDivElement | null>(null)
  const [inputValue, setInputValue] = useState('')
  const initialPages = ['home', page].filter((i): i is PageType => Boolean(i))
  const [pages, setPages] = useState<PageType[]>(initialPages)
  const activePage = pages[pages.length - 1]
  const isHome = activePage === 'home'
  const isActivePageReports = activePage === 'reports'

  const popPage = useCallback(() => {
    setPages((pages) => {
      const x = [...pages]
      x.splice(-1, 1)
      return x
    })
  }, [])

  function bounce() {
    if (ref.current) {
      ref.current.style.transform = 'scale(0.96)'
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.transform = ''
        }
      }, 100)

      setInputValue('')
    }
  }

  return (
    <ProvideCommandRoute onRoute={handleReroute}>
      <div className="cmdk-citrus">
        <Command
          ref={ref}
          shouldFilter={isHome || isActivePageReports}
          loop
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              bounce()
            }

            if (isHome || inputValue.length) {
              return
            }

            if (e.key === 'Backspace') {
              e.preventDefault()
              popPage()
              bounce()
            }
          }}
        >
          <div>
            {pages.map((p) => (
              <div key={p} cmdk-citrus-badge="">
                {p}
              </div>
            ))}
          </div>
          <Command.Input
            autoFocus
            placeholder="How can we help?"
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value)
            }}
          />
          <Command.List>
            {activePage === 'home' && <Command.Empty>No results found.</Command.Empty>}
            {activePage === 'home' && (
              <Home
                onSearchOrders={() => {
                  setInputValue('')
                  setPages([...pages, 'orders'])
                }}
                onSearchTaxRates={() => {
                  setInputValue('')
                  setPages([...pages, 'tax rates'])
                }}
                onSearchCoupons={() => {
                  setInputValue('')
                  setPages([...pages, 'coupons'])
                }}
                onSearchMemberships={() => {
                  setInputValue('')
                  setPages([...pages, 'memberships'])
                }}
                onSearchEvents={() => {
                  setInputValue('')
                  setPages([...pages, 'events'])
                }}
                onSearchAppointmentTypes={() => {
                  setInputValue('')
                  setPages([...pages, 'appointment types'])
                }}
                onSearchContacts={() => {
                  setInputValue('')
                  setPages([...pages, 'contacts'])
                }}
                onSearchAttendees={() => {
                  setInputValue('')
                  setPages([...pages, 'attendees'])
                }}
                onSearchConversations={() => {
                  setInputValue('')
                  setPages([...pages, 'conversations'])
                }}
                onSearchReports={() => {
                  setInputValue('')
                  setPages([...pages, 'reports'])
                }}
                onSearchPrograms={() => {
                  setInputValue('')
                  setPages([...pages, 'programs'])
                }}
              />
            )}
            {activePage === 'orders' && isProductsEnabled && <OrdersV2 />}
            {activePage === 'orders' && !isProductsEnabled && <Orders />}
            {activePage === 'tax rates' && isProductsEnabled && <TaxRatesV2 />}
            {activePage === 'tax rates' && !isProductsEnabled && <TaxRates />}
            {activePage === 'coupons' && isProductsEnabled && <CouponsV2 />}
            {activePage === 'coupons' && !isProductsEnabled && <Coupons />}
            {activePage === 'memberships' && <Memberships />}
            {activePage === 'events' && <Events />}
            {activePage === 'appointment types' && <AppointmentTypes />}
            {activePage === 'contacts' && <Contacts />}
            {activePage === 'attendees' && <Attendees />}
            {activePage === 'conversations' && <Conversations />}
            {activePage === 'reports' && <Reports />}
            {activePage === 'programs' && <Programs />}
          </Command.List>
        </Command>
      </div>
    </ProvideCommandRoute>
  )
}

interface IHomeProps {
  onSearchOrders: Function
  onSearchTaxRates: Function
  onSearchCoupons: Function
  onSearchMemberships: Function
  onSearchEvents: Function
  onSearchAppointmentTypes: Function
  onSearchContacts: Function
  onSearchAttendees: Function
  onSearchConversations: Function
  onSearchReports: Function
  onSearchPrograms: Function
}

const Home = ({
  onSearchOrders: searchOrders,
  onSearchTaxRates: searchTaxRates,
  onSearchCoupons: searchCoupons,
  onSearchMemberships: searchMemberships,
  onSearchEvents: searchEvents,
  onSearchAppointmentTypes: searchAppointmentTypes,
  onSearchContacts: searchContacts,
  onSearchAttendees: searchAttendees,
  onSearchConversations: searchConversations,
  onSearchReports: searchReports,
  onSearchPrograms: searchPrograms,
}: IHomeProps) => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  return (
    <>
      <Command.Group heading="Orders">
        <Item onSelect={() => searchOrders()}>
          <FontAwesomeIcon icon={faFileInvoiceDollar} fixedWidth className="me-2" />
          Search orders...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.FINANCIALS_ORDER_CREATE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Create new order...
        </Item>
      </Command.Group>
      <Command.Group heading="Contacts">
        <Item onSelect={() => searchContacts()}>
          <FontAwesomeIcon icon={faAddressBook} fixedWidth className="me-2" />
          Search contacts...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.CONTACTS_CREATE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Create new contact...
        </Item>
      </Command.Group>
      <Command.Group heading="Attendees">
        <Item onSelect={() => searchAttendees()}>
          <FontAwesomeIcon icon={faRunning} fixedWidth className="me-2" />
          Search attendees...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.ATTENDEES_CREATE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Create new attendee...
        </Item>
      </Command.Group>
      <Command.Group heading="Events">
        <Item onSelect={() => searchEvents()}>
          <FontAwesomeIcon icon={faCalendarStar} fixedWidth className="me-2" />
          Search events...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.EVENT_CREATE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Create new event...
        </Item>
      </Command.Group>
      <FeatureFlag flagKey="memberships_enabled">
        <Command.Group heading="Memberships">
          <Item onSelect={() => searchMemberships()}>
            <FontAwesomeIcon icon={faSync} fixedWidth className="me-2" />
            Search memberships...
          </Item>
          <Item onSelect={() => handleRoute(ROUTES.FINANCIALS_MEMBERSHIPS_CREATE)}>
            <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
            Create new membership...
          </Item>
        </Command.Group>
      </FeatureFlag>
      <FeatureFlag flagKey="appointments_enabled">
        <Command.Group heading="Appointment Types">
          <Item onSelect={() => searchAppointmentTypes()}>
            <FontAwesomeIcon icon={faCalendarCheck} fixedWidth className="me-2" />
            Search appointment types...
          </Item>
          <Item onSelect={() => handleRoute(ROUTES.APPOINTMENT_TYPE_CREATE)}>
            <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
            Create new appointment type...
          </Item>
        </Command.Group>
      </FeatureFlag>
      <FeatureFlag flagKey="communications_enabled">
        <Command.Group heading="Inbox">
          <Item onSelect={() => searchConversations()}>
            <FontAwesomeIcon icon={faInbox} fixedWidth className="me-2" />
            Search conversations...
          </Item>
          <Item onSelect={() => handleRoute(ROUTES.INBOX_CONVERSATION_COMPOSE)}>
            <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
            Compose new message...
          </Item>
        </Command.Group>
      </FeatureFlag>
      <Command.Group heading="Team">
        <Item onSelect={() => handleRoute(ROUTES.TEAM)}>
          <FontAwesomeIcon icon={faUserFriends} fixedWidth className="me-2" />
          View team members...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.TEAM_INVITE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Invite team members...
        </Item>
      </Command.Group>
      <FeatureFlag flagKey="discounts_enabled">
        <Command.Group heading="Coupons">
          <Item onSelect={() => searchCoupons()}>
            <FontAwesomeIcon icon={faBadgePercent} fixedWidth className="me-2" />
            Search coupons...
          </Item>
          <Item onSelect={() => handleRoute(ROUTES.FINANCIALS_COUPON_CREATE)}>
            <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
            Create new coupon...
          </Item>
        </Command.Group>
      </FeatureFlag>
      <Command.Group heading="Tax Rates">
        <Item onSelect={() => searchTaxRates()}>
          <FontAwesomeIcon icon={faHandHoldingDollar} fixedWidth className="me-2" />
          Search tax rates...
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.FINANCIALS_TAX_RATE_CREATE)}>
          <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2" />
          Create new tax rate...
        </Item>
      </Command.Group>
      <Command.Group heading="Personal">
        <Item onSelect={() => handleRoute(ROUTES.PROFILE_SETTINGS)}>
          <FontAwesomeIcon icon={faUserCog} fixedWidth={true} className="me-2" />
          Change your profile information
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.CHANGE_PASSWORD)}>
          <FontAwesomeIcon icon={faUnlock} fixedWidth={true} className="me-2" />
          Change your password
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.NOTIFICATION_SETTINGS)}>
          <FontAwesomeIcon icon={faBell} fixedWidth={true} className="me-2" />
          Change your notification settings
        </Item>
      </Command.Group>
      <Command.Group heading="Program">
        <Item onSelect={() => handleRoute(ROUTES.FINANCIALS_PAYOUTS)}>
          <FontAwesomeIcon icon={faBank} fixedWidth={true} className="me-2" />
          View your payouts
        </Item>
        <Item onSelect={() => searchReports()}>
          <FontAwesomeIcon icon={faFileChartColumn} fixedWidth className="me-2" />
          Search reports...
        </Item>
        <Item
          onSelect={() =>
            window.open(`${process.env.REACT_APP_REGISTER_URL}/pg/${program?.id}`, '_blank')
          }
        >
          <FontAwesomeIcon icon={faExternalLink} fixedWidth={true} className="me-2" />
          View public storefront
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.PROGRAM_SETTINGS)}>
          <FontAwesomeIcon icon={faCogs} fixedWidth={true} className="me-2" />
          Change your program details
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.WAIVER_SETTINGS)}>
          <FontAwesomeIcon icon={faFileContract} fixedWidth={true} className="me-2" />
          Manage your program waivers and terms
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.REFUND_POLICY_SETTINGS)}>
          <span className="fa-layers fa-fw me-2">
            <FontAwesomeIcon icon={faFile} />
            <FontAwesomeIcon icon={faUndoAlt} transform="shrink-9 down-1" />
          </span>
          Select your cancellation and refund policies
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.PACKAGES)}>
          <FontAwesomeIcon icon={faTags} fixedWidth={true} className="me-2" />
          Configure your membership and session packages
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.BRANDING_SETTINGS)}>
          <FontAwesomeIcon icon={faShoppingCart} fixedWidth={true} className="me-2" />
          Change your checkout branding
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.EVENT_SETTINGS)}>
          <FontAwesomeIcon icon={faCalendarCircleUser} fixedWidth={true} className="me-2" />
          Manage how registrations work with events
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.FILTER_SETTINGS)}>
          <FontAwesomeIcon icon={faFilter} fixedWidth={true} className="me-2" />
          Add quick filters to your checkout
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.CONNECT_SETTINGS)}>
          <FontAwesomeIcon icon={faBrowser} fixedWidth={true} className="me-2" />
          Connect your storefront to your website
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.PAYOUT_ACCOUNT)}>
          <FontAwesomeIcon icon={faMoneyCheck} fixedWidth={true} className="me-2" />
          Configure payments and payout information
        </Item>
        <FeatureFlag flagKey="payment_enhancements_enabled">
          <Item onSelect={() => handleRoute(ROUTES.TERMINALS)}>
            <FontAwesomeIcon icon={faMobileScreen} fixedWidth={true} className="me-2" />
            Setup and manage in-person payment terminals
          </Item>
        </FeatureFlag>
        <Item onSelect={() => handleRoute(ROUTES.BILLING_SETTINGS)}>
          <FontAwesomeIcon icon={faRocket} fixedWidth={true} className="me-2" />
          Upgrade your billing plan
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.PROGRAM_ACTIVITY)}>
          <FontAwesomeIcon icon={faListAlt} fixedWidth={true} className="me-2" />
          View your program's activity
        </Item>
        <Item onSelect={() => handleRoute(ROUTES.IMPORT)}>
          <FontAwesomeIcon icon={faFileImport} fixedWidth={true} className="me-2" />
          Import contact, attendee, registration, events, or product information into Citrus
        </Item>
        <Item onSelect={() => searchPrograms()}>
          <FontAwesomeIcon icon={faUserGroupSimple} fixedWidth={true} className="me-2" />
          Switch to another program...
        </Item>
      </Command.Group>
    </>
  )
}

const Orders = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: orders,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteOrders({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {orders?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon
                  icon={faFileInvoiceDollar}
                  fixedWidth={true}
                  className="me-2 my-auto"
                />
                <div>
                  <b>{item.receipt_number}</b> <span>{item.amount_total.display}</span>{' '}
                  <OrderStatusBadge order={item} />{' '}
                  <div className="text-body-secondary small w-100">
                    {formatFullName(item.contact)} ({item.contact.email})
                  </div>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const TaxRates = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: taxRates,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteTaxRates({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {taxRates?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_TAX_RATE_DETAIL.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon
                  icon={faHandHoldingDollar}
                  fixedWidth={true}
                  className="me-2 my-auto"
                />
                <div>
                  <TruncatedText>
                    <b className="w-100">
                      {item.display_name}
                      {' · '}
                      {item.percentage}%
                    </b>
                  </TruncatedText>
                  <div className="text-body-secondary small w-100">
                    Applies to:{' '}
                    {formatList(item.applies_to.map((a) => `${formatDefaultLabel(a)}s`))}
                  </div>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Coupons = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: coupons,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteCoupons({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {coupons?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_COUPON_DETAILS.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faBadgePercent} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{item.code}</b>
                  </TruncatedText>{' '}
                  <TruncatedText className="text-body-secondary small w-100">
                    <CouponDescription coupon={item} programId={item?.program_id} show="amount" />
                    {' · '}
                    <CouponDescription
                      coupon={item}
                      programId={item?.program_id}
                      show="restrictions"
                    />
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Memberships = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: memberships,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteMemberships({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {memberships?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() =>
              handleRoute(ROUTES.FINANCIALS_MEMBERSHIP_DETAIL.replace(':id', item.id))
            }
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faSync} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">
                      {item.package.name} <MembershipStatusBadge membership={item} />
                    </b>
                  </TruncatedText>
                  <TruncatedText className="text-body-secondary small w-100">
                    {formatFullName(item.program_attendee)}
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Events = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: events,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteEvents({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {events?.map((item) => (
        <Fragment key={item.id}>
          <Item onSelect={() => handleRoute(ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', item.id))}>
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faCalendarStar} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{item.name}</b>
                  </TruncatedText>{' '}
                  <TruncatedText className="text-body-secondary small w-100">
                    {' '}
                    {(() => {
                      const zone = item.timezone
                      const start_at = DateTime.fromJSDate(new Date(item.starts_at), {
                        zone,
                      })
                      const ends_at = DateTime.fromJSDate(new Date(item.ends_at), {
                        zone,
                      })
                      return start_at.hasSame(ends_at, 'day')
                        ? `${start_at.toFormat(`MMMM d yyyy, t`)} - ${ends_at.toFormat(`t ZZZZ`)}`
                        : `${start_at.toFormat(`MMMM d yyyy, t`)} - ${ends_at.toFormat(
                            `MMMM d yyyy, t ZZZZ`,
                          )}`
                    })()}
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const AppointmentTypes = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: types,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteAppointmentTypes({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {types?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.APPOINTMENT_EDIT_DASHBOARD.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon
                  icon={faCalendarCheck}
                  fixedWidth={true}
                  className="me-2 my-auto"
                />
                <div>
                  <TruncatedText>
                    <b className="w-100">
                      {item.name} <AppointmentTypeStatusBadge status={item.status} />
                    </b>
                  </TruncatedText>
                  <TruncatedText className="text-body-secondary small w-100">
                    <AppointmentSchedulingText appointmentType={item} />
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Contacts = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: contacts,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteContacts({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {contacts?.map((item) => (
        <Fragment key={item.id}>
          <Item onSelect={() => handleRoute(ROUTES.CONTACT_DETAILS.replace(':id', item.id))}>
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faAddressBook} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{formatFullName(item)}</b>
                  </TruncatedText>
                  <TruncatedText className="text-body-secondary small w-100">
                    {item.email}
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Attendees = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: attendees,
    isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteAttendees({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {attendees?.map((item) => (
        <Fragment key={item.id}>
          <Item onSelect={() => handleRoute(ROUTES.ATTENDEE_DETAILS.replace(':id', item.id))}>
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faRunning} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{formatFullName(item)}</b>
                  </TruncatedText>{' '}
                  <TruncatedText className="text-body-secondary small w-100">
                    {[formatGender(item), formatAge(item)].filter(Boolean).join(' · ')}
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Conversations = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  const {
    data: conversations,
    isFetching: isLoading,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchConversations({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useEffect(() => {
    setSearch(search)
  }, [search, setSearch])
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {conversations?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.INBOX_CONVERSATION_DETAILS.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faInbox} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{item.subject}</b>
                  </TruncatedText>{' '}
                  <TruncatedText className="text-body-secondary small w-100">
                    {formatFullName(item.last_message?.contact)}
                  </TruncatedText>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {!!item.last_message &&
                  DateTime.fromJSDate(
                    item.last_message.sent ||
                      item.last_message.updated ||
                      item.last_message.created,
                  ).toLocaleString(DateTime.DATE_MED)}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const Reports = () => {
  const { handleRoute } = useCommandReroute()
  return (
    <>
      {FinancialReportList?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_REPORT_DETAIL.replace(':type', item.id))}
          >
            <FontAwesomeIcon icon={faFileChartColumn} fixedWidth className="me-2" />
            {item.name}
          </Item>
        </Fragment>
      ))}
    </>
  )
}
const Programs = () => {
  const { data: members } = useFetchUserMembers({})
  const { setProgramId } = useAuth()
  const { handleRoute } = useCommandReroute()
  const search = useCommandState((state) => state.search)
  return (
    <>
      {members
        ?.filter((item) => item.program.name.toLowerCase().includes(search.toLowerCase()))
        .map((item) => (
          <Fragment key={item.id}>
            <Item onSelect={() => setProgramId(item.program.id)}>
              <Icon
                src={item.program.preferences?.icon}
                alt={item.program.name}
                className="me-2 rounded"
                height={16}
                width={16}
              />
              {item.program.name}
            </Item>
          </Fragment>
        ))}
      <Item onSelect={() => handleRoute(ROUTES.SETUP)}>
        <FontAwesomeIcon icon={faPlusCircle} fixedWidth className="me-2" />
        Create new program
      </Item>
    </>
  )
}

const OrdersV2 = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const {
    data: orders,
    isLoading,
    search,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteOrdersV2({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useCommandState((state) => {
    if (state.search !== search) setSearch(state.search)
  })
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {orders?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon
                  icon={faFileInvoiceDollar}
                  fixedWidth={true}
                  className="me-2 my-auto"
                />
                <div>
                  <b>{item.receipt_number}</b> <span>{item.amount_total.display}</span>{' '}
                  <OrderStatusBadgeV2 order={item} />{' '}
                  <div className="text-body-secondary small w-100">
                    {item.contact_name} ({item.contact_email})
                  </div>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const TaxRatesV2 = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const {
    data: taxRates,
    isLoading,
    search,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteTaxRatesV2({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useCommandState((state) => {
    if (state.search !== search) setSearch(state.search)
  })
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {taxRates?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_TAX_RATE_DETAIL.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon
                  icon={faHandHoldingDollar}
                  fixedWidth={true}
                  className="me-2 my-auto"
                />
                <div>
                  <TruncatedText>
                    <b className="w-100">
                      {item.display_name}
                      {' · '}
                      {item.percentage}%
                    </b>
                  </TruncatedText>
                  <div className="text-body-secondary small w-100">
                    Applies to:{' '}
                    {formatList(
                      (item.applies_to.product_categories || []).map(
                        (a) => `${formatDefaultLabel(a)}s`,
                      ),
                    )}
                  </div>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}
const CouponsV2 = () => {
  const { program } = useAuth()
  const { handleRoute } = useCommandReroute()
  const {
    data: coupons,
    isLoading,
    search,
    setSearch,
    pagination,
    fetchNextPage,
  } = useFetchInfiniteCouponsV2({
    programId: program?.id,
    pagination: DefaultDozenPagination,
  })
  useCommandState((state) => {
    if (state.search !== search) setSearch(state.search)
  })
  const { count, total, page, page_count = 0 } = pagination
  const hasMore = !(page + 1 >= page_count || count >= total)
  return (
    <>
      {coupons?.map((item) => (
        <Fragment key={item.id}>
          <Item
            onSelect={() => handleRoute(ROUTES.FINANCIALS_COUPON_DETAILS.replace(':id', item.id))}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex">
                <FontAwesomeIcon icon={faBadgePercent} fixedWidth={true} className="me-2 my-auto" />
                <div>
                  <TruncatedText>
                    <b className="w-100">{item.code}</b>
                  </TruncatedText>
                  <CouponDescriptionV2
                    className="text-body-secondary small w-100"
                    coupon={item}
                    show="in-line"
                  />
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromJSDate(item.updated || item.created).toLocaleString(
                  DateTime.DATE_MED,
                )}
              </div>
            </div>
          </Item>
        </Fragment>
      ))}
      {!isLoading && hasMore && (
        <Item onSelect={() => fetchNextPage()}>
          <div className="text-center w-100">See more</div>
        </Item>
      )}
      {!isLoading && <Command.Empty>No results found.</Command.Empty>}
      {isLoading && <Command.Loading>Searching...</Command.Loading>}
    </>
  )
}

function Item({
  children,
  shortcut,
  onSelect = () => {},
}: {
  children: React.ReactNode
  shortcut?: string
  onSelect?: (value: string) => void
}) {
  return (
    <Command.Item onSelect={onSelect}>
      {children}
      {shortcut && (
        <div cmdk-citrus-shortcuts="">
          {shortcut.split(' ').map((key) => {
            return <kbd key={key}>{key}</kbd>
          })}
        </div>
      )}
    </Command.Item>
  )
}
